import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { getFirebaseFirestore } from '../firebase/index';

export interface UserProfileRecord {
  id: string;
  name: string;
  email: string;
}

const USERS_COLLECTION = 'users';

export const upsertUserProfile = async (profile: UserProfileRecord) => {
  const db = await getFirebaseFirestore();
  const userRef = doc(db, USERS_COLLECTION, profile.id);
  await setDoc(
    userRef,
    {
      name: profile.name,
      email: profile.email,
      updatedAt: new Date().toISOString(),
    },
    { merge: true },
  );
};

export const getUserByEmail = async (email: string): Promise<UserProfileRecord | null> => {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }
  const db = await getFirebaseFirestore();
  const usersRef = collection(db, USERS_COLLECTION);
  const usersQuery = query(usersRef, where('email', '==', trimmed));
  const snapshot = await getDocs(usersQuery);
  if (snapshot.empty) {
    return null;
  }
  const docSnap = snapshot.docs[0];
  const data = docSnap.data() as DocumentData;
  return {
    id: docSnap.id,
    name: data.name ?? trimmed,
    email: data.email ?? trimmed,
  };
};

const chunkArray = <T,>(array: T[], size: number): T[][] => {
  if (size <= 0) return [array];
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

export const getUsersByIds = async (ids: string[]): Promise<UserProfileRecord[]> => {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
  if (uniqueIds.length === 0) {
    return [];
  }
  const db = await getFirebaseFirestore();
  const usersRef = collection(db, USERS_COLLECTION);
  const results: UserProfileRecord[] = [];

  const chunks = chunkArray(uniqueIds, 10);
  for (const chunk of chunks) {
    const q = query(usersRef, where('__name__', 'in', chunk));
    const snapshot = await getDocs(q);
    snapshot.forEach(docSnap => {
      const data = docSnap.data() as DocumentData;
      results.push({
        id: docSnap.id,
        name: data.name ?? fallbackDisplayName(data.email),
        email: data.email ?? '',
      });
    });
  }
  return results;
};

const fallbackDisplayName = (email?: string): string => {
  if (!email) return 'Member';
  const [namePart] = email.split('@');
  return namePart || 'Member';
};
